import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Phone, Mail, Building2, Plus, Loader2, Flame, Zap, Snowflake, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const INTERACTION_TYPES = [
  {value:'call_out', label:'Chiamata Uscente'},
  {value:'call_in', label:'Chiamata Entrante'},
  {value:'email_sent', label:'Email Inviata'},
  {value:'email_received', label:'Email Ricevuta'},
  {value:'meeting', label:'Riunione'},
  {value:'note', label:'Nota'},
];

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInteraction, setShowInteraction] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [intForm, setIntForm] = useState({ type: 'note', subject: '', content: '' });
  const [saving, setSaving] = useState(false);

  const fetchContact = async () => {
    try {
      const res = await axios.get(`${API}/contacts/${id}`);
      setContact(res.data);
    } catch { toast.error('Contatto non trovato'); navigate('/contacts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchContact(); }, [id]);

  const addInteraction = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/interactions`, { ...intForm, contact_id: id });
      toast.success('Interazione registrata');
      setShowInteraction(false);
      setIntForm({ type: 'note', subject: '', content: '' });
      fetchContact();
    } catch { toast.error('Errore'); } finally { setSaving(false); }
  };

  const runLeadScore = async () => {
    setScoring(true);
    try {
      const res = await axios.post(`${API}/ai/lead-score`, { contact_id: id });
      toast.success(`Lead Score: ${res.data.score}/100 (${res.data.label})`);
      fetchContact();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore AI');
    } finally { setScoring(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!contact) return null;

  const scoreLabel = contact.lead_score_label;
  const ScoreIcon = scoreLabel === 'Hot' ? Flame : scoreLabel === 'Warm' ? Zap : Snowflake;
  const scoreClass = scoreLabel === 'Hot' ? 'score-hot' : scoreLabel === 'Warm' ? 'score-warm' : 'score-cold';

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/contacts')}><ArrowLeft size={18} /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{fontFamily:'Manrope, sans-serif'}}>{contact.first_name} {contact.last_name}</h1>
            {contact.lead_score && (
              <span className={`inline-flex items-center gap-1 text-sm px-2.5 py-0.5 rounded-full font-semibold ${scoreClass}`}>
                <ScoreIcon size={12} />{contact.lead_score}/100
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-sm">{contact.position || 'Contatto'}{contact.company?.name ? ` • ${contact.company.name}` : ''}</p>
        </div>
        <Button variant="outline" size="sm" onClick={runLeadScore} disabled={scoring} data-testid="lead-score-btn">
          {scoring ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-2" />}
          AI Score
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Informazioni</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {contact.email && <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-muted-foreground" /><a href={`mailto:${contact.email}`} className="text-primary hover:underline">{contact.email}</a></div>}
              {contact.phone && <div className="flex items-center gap-2 text-sm"><Phone size={14} className="text-muted-foreground" />{contact.phone}</div>}
              {contact.mobile && <div className="flex items-center gap-2 text-sm"><Phone size={14} className="text-muted-foreground" />{contact.mobile} (mobile)</div>}
              {contact.company && <div className="flex items-center gap-2 text-sm"><Building2 size={14} className="text-muted-foreground" /><button className="text-primary hover:underline" onClick={() => navigate(`/companies/${contact.company.id}`)}>{contact.company.name}</button></div>}
              {contact.tags?.length > 0 && <div className="flex flex-wrap gap-1">{contact.tags.map(t => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}</div>}
              {contact.notes && <p className="text-sm text-muted-foreground">{contact.notes}</p>}
              {contact.last_contacted_at && (
                <p className="text-xs text-muted-foreground">Ultimo contatto: {new Date(contact.last_contacted_at).toLocaleDateString('it-IT')}</p>
              )}
            </CardContent>
          </Card>

          {contact.opportunities?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Opportunità</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {contact.opportunities.map(opp => (
                  <div key={opp._id||opp.id} className="flex items-center justify-between text-sm">
                    <span>{opp.title}</span>
                    <Badge variant="outline" className="text-xs">{opp.stage}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Storico Interazioni</CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowInteraction(true)}>
                  <Plus size={13} className="mr-1.5" />Aggiungi
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {contact.interactions?.length > 0 ? (
                <div className="space-y-3">
                  {contact.interactions.map((int, i) => (
                    <div key={int._id||int.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                        {i < contact.interactions.length - 1 && <div className="w-0.5 flex-1 bg-border mt-1" />}
                      </div>
                      <div className="pb-3 flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium truncate">{int.subject}</p>
                          <span className="text-xs text-muted-foreground flex-shrink-0">{new Date(int.date).toLocaleDateString('it-IT')}</span>
                        </div>
                        {int.content && <p className="text-xs text-muted-foreground mt-0.5">{int.content}</p>}
                        <Badge variant="outline" className="text-xs mt-1">{int.type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground py-4">Nessuna interazione. Inizia aggiungendone una!</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showInteraction} onOpenChange={setShowInteraction}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuova Interazione</DialogTitle></DialogHeader>
          <form onSubmit={addInteraction} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={intForm.type} onValueChange={v => setIntForm(f => ({...f, type:v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INTERACTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Oggetto *</Label>
              <Input value={intForm.subject} onChange={e => setIntForm(f => ({...f, subject:e.target.value}))} required />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={intForm.content} onChange={e => setIntForm(f => ({...f, content:e.target.value}))} rows={3} />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowInteraction(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
